import { useMemo, useState } from "react";
import { Button } from "./ui/Button";
import { Badge } from "./ui/Badge";
import { Tooltip } from "./ui/Tooltip";
import { Dialog, DialogTitle, DialogDescription } from "./ui/Dialog";
import AddLocationDialog from "./AddLocationDialog";
import { displayLicenseName } from "../lib/display-name";
import LicenseRow from "./LicenseRow";

export interface ChainMember {
  license_number: string;
  license_type: string | null;
  dba_name: string | null;
  legal_name: string | null;
  display_name: string | null;
  gbp_display_name?: string | null;
  premise_city: string | null;
  premise_street: string | null;
  premise_zip?: string | null;
  is_active: boolean | null;
  status: string | null;
  // "Why Matched?" audit fields (populated by the candidates endpoints).
  dba_stem?: string | null;
  legal_stem?: string | null;
  matched_on?: ("dba" | "legal")[];
}

export interface ChainCandidate {
  candidate_id: string;
  canonical_root: string;
  candidate_display_name: string;
  member_license_numbers: string[];
  manually_added_license_numbers?: string[];
  staged_aliases?: Array<{ alias: string; source: string; license_number?: string }>;
  member_count_c10: number;
  member_count_c9: number;
  member_count_c12: number;
  distinct_cities: number;
  flag_count: number;
  flagged_by_brand_id: string | null;
  decision:
    | "pending"
    | "approved"
    | "rejected"
    | "merged"
    | "auto_approve_eligible"
    | "auto_merge_eligible";
  decided_at: string | null;
  approved_chain_id: string | null;
  notes: string | null;
  // PR #28 detector v5 LLM judge fields (null until analyzed).
  llm_verdict?: "new_chain" | "merge_into" | "not_a_chain" | null;
  llm_reasoning?: string | null;
  llm_confidence?: number | null;
  llm_proposed_name?: string | null;
  llm_merge_into_chain_id?: string | null;
  llm_model?: string | null;
  llm_analyzed_at?: string | null;
  first_detected_at: string;
  last_detected_at: string;
  total_members?: number;
  members?: ChainMember[];
}

export interface MergeTarget {
  kind: "candidate" | "chain";
  id: string;
  label: string;
  sublabel?: string;
}

interface Props {
  candidate: ChainCandidate;
  mergeTargets: MergeTarget[];
  onApprove: (
    candidate: ChainCandidate,
    payload: { display_name: string; website?: string; notes?: string; member_license_numbers_to_include: string[] }
  ) => Promise<void>;
  onReject: (candidate: ChainCandidate, reason: string) => Promise<void>;
  onMerge: (
    candidate: ChainCandidate,
    payload: { target_candidate_id?: string; target_chain_id?: string }
  ) => Promise<void>;
  onSplit: (candidate: ChainCandidate, license_numbers_to_remove: string[]) => Promise<void>;
  onSkip: (candidate: ChainCandidate) => void;
  onLocationAdded?: (candidate: ChainCandidate) => void;
}

const CLASS_LABEL: Record<string, { key: "c10" | "c9" | "c12"; label: string }> = {
  "Commercial -  Retailer": { key: "c10", label: "STOREFRONT (C10)" },
  "Commercial -  Retailer - Non-Storefront": { key: "c9", label: "DELIVERY (C9)" },
  "Commercial -  Microbusiness": { key: "c12", label: "MICROBUSINESS (C12)" },
};

function classKey(t: string | null): "c10" | "c9" | "c12" | "other" {
  if (!t) return "other";
  return CLASS_LABEL[t]?.key ?? "other";
}

function decisionBadge(d: ChainCandidate["decision"]) {
  switch (d) {
    case "approved": return { variant: "auto" as const, label: "APPROVED" };
    case "rejected": return { variant: "unmatched" as const, label: "REJECTED" };
    case "merged":   return { variant: "neutral" as const, label: "MERGED" };
    case "auto_approve_eligible": return { variant: "auto" as const, label: "AUTO-APPROVE ELIGIBLE" };
    case "auto_merge_eligible":   return { variant: "auto" as const, label: "AUTO-MERGE ELIGIBLE" };
    default:         return { variant: "review" as const, label: "PENDING" };
  }
}

/**
 * One-line source attribution for the proposed chain name. When the LLM judge
 * (detector v5) named the candidate we credit the model + confidence; otherwise
 * the name came from the detector's canonical-stem rule.
 */
function sourceAttribution(candidate: ChainCandidate): string {
  if (candidate.llm_proposed_name && candidate.llm_verdict) {
    const conf =
      typeof candidate.llm_confidence === "number"
        ? ` · confidence ${candidate.llm_confidence.toFixed(2)}`
        : "";
    const model = candidate.llm_model ? ` (${candidate.llm_model})` : "";
    return `proposed by LLM${model}${conf}`;
  }
  return `proposed by detector rule · stem '${candidate.canonical_root}'`;
}

/** Render the detector-v5 LLM verdict block, if the candidate was analyzed. */
function LlmVerdictBlock({ candidate }: { candidate: ChainCandidate }) {
  if (!candidate.llm_verdict) return null;
  const conf =
    typeof candidate.llm_confidence === "number"
      ? `${Math.round(candidate.llm_confidence * 100)}%`
      : "—";
  const verdictColor =
    candidate.llm_verdict === "not_a_chain"
      ? "text-[#991B1B] bg-[#FEF2F2] border-[#FECACA]"
      : "text-[#1C5125] bg-[#F1F8F2] border-[#BFE0C4]";
  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${verdictColor}`}>
          LLM: {candidate.llm_verdict.replace(/_/g, " ")}
        </span>
        <span className="text-xs text-gray-600">confidence <span className="font-semibold text-gray-800">{conf}</span></span>
        {candidate.llm_proposed_name && (
          <span className="text-xs text-gray-600">name <span className="font-medium text-gray-800">{candidate.llm_proposed_name}</span></span>
        )}
        {candidate.llm_model && <span className="text-[11px] text-gray-400">{candidate.llm_model}</span>}
      </div>
      {candidate.llm_reasoning && (
        <p className="mt-2 text-xs leading-relaxed text-gray-600">{candidate.llm_reasoning}</p>
      )}
    </div>
  );
}

function formatTs(iso: string | null) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ChainCandidateCard({
  candidate, mergeTargets, onApprove, onReject, onMerge, onSplit, onSkip, onLocationAdded,
}: Props) {
  const members = candidate.members ?? [];
  const total = candidate.member_count_c10 + candidate.member_count_c9 + candidate.member_count_c12;
  const dec = decisionBadge(candidate.decision);

  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(candidate.member_license_numbers));
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const [showFlags, setShowFlags] = useState(false);

  const [displayName, setDisplayName] = useState(candidate.candidate_display_name);
  const [website, setWebsite] = useState("");
  const [approveNotes, setApproveNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [mergeTargetKey, setMergeTargetKey] = useState<string>("");

  const grouped = useMemo(() => {
    const out = { c10: [] as ChainMember[], c9: [] as ChainMember[], c12: [] as ChainMember[], other: [] as ChainMember[] };
    for (const m of members) {
      const k = classKey(m.license_type);
      out[k].push(m);
    }
    return out;
  }, [members]);

  const toggle = (lic: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lic)) next.delete(lic); else next.add(lic);
      return next;
    });
  };
  const toggleClass = (cls: "c10" | "c9" | "c12" | "other", check: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const m of grouped[cls]) {
        if (check) next.add(m.license_number);
        else next.delete(m.license_number);
      }
      return next;
    });
  };

  // "Why we matched these" — aggregate which DCC field stemmed to canonical_root.
  // A license can match on both DBA and legal; count each field independently.
  const matchBreakdown = useMemo(() => {
    let dba = 0;
    let legal = 0;
    let unknown = 0;
    for (const m of members) {
      const on = m.matched_on ?? [];
      if (on.includes("dba")) dba += 1;
      if (on.includes("legal")) legal += 1;
      if (on.length === 0) unknown += 1;
    }
    return { dba, legal, unknown };
  }, [members]);

  const removed = useMemo(
    () => candidate.member_license_numbers.filter((l) => !selected.has(l)),
    [candidate.member_license_numbers, selected]
  );
  const splitDisabled = removed.length === 0 || selected.size === 0;

  const runApprove = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      await onApprove(candidate, {
        display_name: displayName.trim() || candidate.candidate_display_name,
        website: website.trim() || undefined,
        notes: approveNotes.trim() || undefined,
        member_license_numbers_to_include: Array.from(selected),
      });
      setApproveOpen(false);
    } finally {
      setBusy(false);
    }
  };
  const runReject = async () => {
    setBusy(true);
    try {
      await onReject(candidate, rejectReason.trim() || "rejected by admin");
      setRejectOpen(false);
    } finally {
      setBusy(false);
    }
  };
  const runMerge = async () => {
    if (!mergeTargetKey) return;
    const [kind, id] = mergeTargetKey.split(":");
    setBusy(true);
    try {
      if (kind === "chain") await onMerge(candidate, { target_chain_id: id });
      else await onMerge(candidate, { target_candidate_id: id });
      setMergeOpen(false);
    } finally {
      setBusy(false);
    }
  };
  const runSplit = async () => {
    setBusy(true);
    try { await onSplit(candidate, removed); }
    finally { setBusy(false); }
  };

  const renderClassSection = (cls: "c10" | "c9" | "c12", subheader: string) => {
    const list = grouped[cls];
    if (list.length === 0) return null;
    const allChecked = list.every((m) => selected.has(m.license_number));
    return (
      <div key={cls} className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <h5 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {subheader} <span className="text-gray-400">({list.length})</span>
          </h5>
          <button
            type="button"
            onClick={() => toggleClass(cls, !allChecked)}
            className="text-[11px] text-accent-700 hover:underline"
          >
            {allChecked ? "Deselect all" : "Select all"}
          </button>
        </div>
        <ul className="space-y-2">
          {list.map((m) => {
            const checked = selected.has(m.license_number);
            // ⚠ when the GBP/Places display_name doesn't reference the matched
            // stem — the match is on the DCC DBA/legal name, not the visible name.
            const root = candidate.canonical_root.trim().toLowerCase();
            const gbpName = m.gbp_display_name || m.display_name || "";
            const displayMentionsRoot = !!root && gbpName.toLowerCase().includes(root);
            const showWarn = !!gbpName && !displayMentionsRoot;
            return (
              <li key={m.license_number}>
                <LicenseRow
                  license={{
                    license_number: m.license_number,
                    gbp_display_name: m.gbp_display_name ?? m.display_name ?? null,
                    dba_name: m.dba_name,
                    legal_name: m.legal_name,
                    premise_city: m.premise_city,
                    premise_zip: m.premise_zip ?? null,
                  }}
                  leading={
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(m.license_number)}
                      aria-label={`include ${m.license_number}`}
                    />
                  }
                  trailing={
                    showWarn ? (
                      <Tooltip
                        content={`Display name doesn't reference '${candidate.canonical_root}'. Matched on DCC DBA/legal. Verify this store is part of the chain.`}
                      >
                        <span className="cursor-help text-amber-500" aria-label="display name does not reference chain stem">⚠</span>
                      </Tooltip>
                    ) : undefined
                  }
                />
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <article className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-base font-semibold text-gray-900">
              {candidate.llm_proposed_name || candidate.candidate_display_name}
            </span>
            <span className="text-xs text-gray-500">{sourceAttribution(candidate)}</span>
          </div>
          <div className="mt-0.5 text-xs text-gray-500">
            <span className="font-medium text-gray-600">auto_detected</span>
            <span className="text-gray-400"> · </span>
            <span>{total} members</span>
            <span className="text-gray-400"> · </span>
            <span>{candidate.distinct_cities} cities</span>
            <span className="text-gray-400"> · </span>
            <span>last detected {formatTs(candidate.last_detected_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {candidate.decision === "approved" && candidate.approved_chain_id && (
            <a
              href={`#/chains/${candidate.approved_chain_id}`}
              className="text-xs font-medium text-accent-700 hover:underline"
            >
              Manage chain →
            </a>
          )}
          <Badge variant={dec.variant}>{dec.label}</Badge>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="rounded-md border border-gray-200 bg-gray-50/60 p-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Candidate chain</h4>
          <dl className="space-y-1.5">
            <div>
              <dt className="text-xs text-gray-500">Stem</dt>
              <dd className="text-base font-semibold text-gray-900">{candidate.canonical_root}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Display name</dt>
              <dd className="text-sm font-medium text-gray-800">{candidate.candidate_display_name}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Members</dt>
              <dd className="text-sm text-gray-800">
                <span className="font-medium">{total}</span>
                <span className="text-gray-500">
                  {" · "}{candidate.member_count_c10} C10
                  {" · "}{candidate.member_count_c9} C9
                  {" · "}{candidate.member_count_c12} C12
                </span>
                {candidate.manually_added_license_numbers && candidate.manually_added_license_numbers.length > 0 && (
                  <span className="ml-2 inline-flex items-center rounded-full border border-accent-200 bg-accent-50 px-2 py-0.5 text-[10px] font-medium text-accent-700">
                    +{candidate.manually_added_license_numbers.length} manual
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Cities</dt>
              <dd className="text-sm text-gray-800">{candidate.distinct_cities}</dd>
            </div>
            {candidate.flag_count > 0 && (
              <div>
                <dt className="text-xs text-gray-500">Brand flags</dt>
                <dd>
                  <button
                    type="button"
                    onClick={() => setShowFlags((s) => !s)}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
                  >
                    🚩 {candidate.flag_count} brand{candidate.flag_count === 1 ? "" : "s"} flagged
                  </button>
                </dd>
              </div>
            )}
          </dl>
        </section>

        <section className="rounded-md border border-gray-200 bg-white p-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Member licenses</h4>
          <div className="max-h-80 overflow-y-auto pr-1">
            {renderClassSection("c10", "STOREFRONT (C10)")}
            {renderClassSection("c9", "DELIVERY (C9)")}
            {renderClassSection("c12", "MICROBUSINESS (C12)")}
          </div>
          <p className="mt-2 text-[11px] text-gray-500">
            {selected.size} of {candidate.member_license_numbers.length} selected
          </p>
        </section>
      </div>

      <LlmVerdictBlock candidate={candidate} />

      <details className="mt-3 group">
        <summary className="cursor-pointer list-none inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
          <span className="inline-block transition-transform group-open:rotate-90">▸</span>
          <span>All names at a glance ({members.length})</span>
        </summary>
        <ul className="mt-2 ml-4 space-y-0.5 text-xs">
          {members.map((m) => (
            <li key={m.license_number} className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium text-gray-800">
                {m.gbp_display_name || m.display_name || (
                  <span className="italic text-gray-400">(no GBP name)</span>
                )}
              </span>
              <span className="font-mono text-[11px] text-gray-400">{m.license_number}</span>
              <span className="text-gray-500">{m.premise_city || "—"}</span>
            </li>
          ))}
        </ul>
      </details>

      <details className="mt-3 group" open>
        <summary className="cursor-pointer list-none inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
          <span className="inline-block transition-transform group-open:rotate-90">▸</span>
          <span>Why we think this is a chain</span>
        </summary>
        <ul className="mt-2 ml-4 list-disc text-xs text-gray-600 space-y-1">
          <li>{total} licenses share canonical stem '<code className="text-gray-800">{candidate.canonical_root}</code>'.</li>
          <li>Members span {candidate.distinct_cities} cities.</li>
          {candidate.flag_count > 0 && (
            <li>{candidate.flag_count} brand{candidate.flag_count === 1 ? "" : "s"} have flagged this as a chain.</li>
          )}
          {(() => {
            const distinct = new Set(members.map((m) => (displayLicenseName(m.display_name, m.dba_name, m.legal_name) || "").trim().toLowerCase()).filter(Boolean));
            if (distinct.size > total * 0.9 && total > 3) {
              return <li className="text-amber-700">⚠ Display names vary widely across members — verify these are the same chain.</li>;
            }
            return null;
          })()}
        </ul>
      </details>

      <details className="mt-2 group">
        <summary className="cursor-pointer list-none inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
          <span className="inline-block transition-transform group-open:rotate-90">▸</span>
          <span>Why we matched these</span>
        </summary>
        <div className="mt-2 ml-4 text-xs text-gray-600 space-y-1">
          <div>
            Matched stem: <code className="text-gray-800">{candidate.canonical_root}</code>
          </div>
          <div>
            Match field breakdown:{" "}
            <span className="text-gray-800">{matchBreakdown.dba}</span> matched on DBA stem
            {" · "}
            <span className="text-gray-800">{matchBreakdown.legal}</span> matched on legal-name stem
            {matchBreakdown.unknown > 0 && (
              <>
                {" · "}
                <span className="text-gray-800">{matchBreakdown.unknown}</span> manually added / other
              </>
            )}
          </div>
          {members.length > 0 && (
            <ul className="mt-1 list-disc ml-4 space-y-0.5">
              {members.map((m) => {
                const on = m.matched_on ?? [];
                const label =
                  on.length === 0
                    ? "manual / no stem match"
                    : on.map((f) => (f === "dba" ? "DBA" : "legal name")).join(" + ");
                return (
                  <li key={m.license_number} className="text-gray-500">
                    <span className="text-gray-700">{displayLicenseName(m.display_name, m.dba_name, m.legal_name) || m.license_number}</span>
                    {" — "}
                    {label}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </details>

      {showFlags && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/50 p-3 text-xs text-amber-900">
          Flagged by {candidate.flag_count} brand{candidate.flag_count === 1 ? "" : "s"}.
          Open the candidate detail view to see flag notes.
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {candidate.decision === "pending" && (
          <>
            <Tooltip content="Approve as a real chain — writes core.retail_chain and links members">
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={() => setApproveOpen(true)}
                disabled={busy || selected.size === 0}
              >
                Approve chain
              </Button>
            </Tooltip>
            <Tooltip content="Add another active retail license to this candidate before approval">
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => setAddLocationOpen(true)}
                disabled={busy}
              >
                + Add Location
              </Button>
            </Tooltip>
            <Tooltip content="Merge into another candidate or an approved chain">
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => setMergeOpen(true)}
                disabled={busy || mergeTargets.length === 0}
              >
                Merge with… ▾
              </Button>
            </Tooltip>
            <Tooltip content="Reject this candidate (not a real chain)">
              <Button
                variant="danger-ghost"
                size="sm"
                type="button"
                onClick={() => setRejectOpen(true)}
                disabled={busy}
              >
                Reject
              </Button>
            </Tooltip>
            <Tooltip content="Split off the unchecked members (removes them from this candidate)">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={runSplit}
                disabled={busy || splitDisabled}
              >
                Split off selected ({removed.length})
              </Button>
            </Tooltip>
            <Tooltip content="Skip — leave for later, no state change">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => onSkip(candidate)}
                disabled={busy}
              >
                Skip
              </Button>
            </Tooltip>
          </>
        )}
      </div>

      <Dialog open={approveOpen} onOpenChange={(v) => !busy && setApproveOpen(v)}>
        <DialogTitle>Approve chain</DialogTitle>
        <DialogDescription>
          Creates a chain in core.retail_chain and links {selected.size} member license{selected.size === 1 ? "" : "s"}.
        </DialogDescription>
        <div className="mt-4 space-y-3">
          <label className="block text-xs">
            <span className="block text-gray-600 mb-1">Display name</span>
            <input
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="block text-xs">
            <span className="block text-gray-600 mb-1">Website (optional)</span>
            <input
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://example.com"
              disabled={busy}
            />
          </label>
          <label className="block text-xs">
            <span className="block text-gray-600 mb-1">Notes (optional)</span>
            <textarea
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
              rows={2}
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={() => setApproveOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" type="button" onClick={runApprove} disabled={busy || selected.size === 0}>
            {busy ? "Approving…" : `Approve ${selected.size} member${selected.size === 1 ? "" : "s"}`}
          </Button>
        </div>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={(v) => !busy && setRejectOpen(v)}>
        <DialogTitle>Reject candidate?</DialogTitle>
        <DialogDescription>
          This candidate will be marked as rejected. You can re-detect later.
        </DialogDescription>
        <div className="mt-4">
          <label className="block text-xs">
            <span className="block text-gray-600 mb-1">Reason</span>
            <textarea
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
              rows={2}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. franchise w/ different ownership"
              disabled={busy}
            />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={() => setRejectOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" type="button" onClick={runReject} disabled={busy}>
            {busy ? "Rejecting…" : "Reject"}
          </Button>
        </div>
      </Dialog>

      <AddLocationDialog
        open={addLocationOpen}
        candidate={candidate}
        onClose={() => setAddLocationOpen(false)}
        onAdded={() => onLocationAdded?.(candidate)}
      />

      <Dialog open={mergeOpen} onOpenChange={(v) => !busy && setMergeOpen(v)}>
        <DialogTitle>Merge candidate</DialogTitle>
        <DialogDescription>
          Merge this candidate's members into another candidate or an existing approved chain.
        </DialogDescription>
        <div className="mt-4">
          <label className="block text-xs">
            <span className="block text-gray-600 mb-1">Target</span>
            <select
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm"
              value={mergeTargetKey}
              onChange={(e) => setMergeTargetKey(e.target.value)}
              disabled={busy}
            >
              <option value="">— pick target —</option>
              {mergeTargets.map((t) => (
                <option key={`${t.kind}:${t.id}`} value={`${t.kind}:${t.id}`}>
                  {t.kind === "chain" ? "Approved: " : "Pending: "}{t.label}
                  {t.sublabel ? ` — ${t.sublabel}` : ""}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={() => setMergeOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" type="button" onClick={runMerge} disabled={busy || !mergeTargetKey}>
            {busy ? "Merging…" : "Merge"}
          </Button>
        </div>
      </Dialog>
    </article>
  );
}
